import {
  PermissionCode,
  type CreateOnboardingTemplateRequest,
  createOnboardingTemplateRequestSchema,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ListPagination } from '@/components/list-pagination';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  onboardingPanelClassName,
  onboardingTableFrameClassName,
} from '@/products/onboarding/features/onboarding-panel';
import { OnboardingTemplateDeleteButton } from '@/products/onboarding/features/onboarding-template-delete-button';
import { ONBOARDING_CASE_KIND_LABEL } from '@/products/onboarding/features/onboarding-template-labels';
import { OnboardingTemplateImportDialog } from '@/products/onboarding/features/onboarding-template-import-dialog';
import { OnboardingTemplateDraftDialog } from '@/products/onboarding/features/onboarding-template-draft-dialog';
import {
  useCreateOnboardingTemplate,
  useCreateOnboardingTemplateFromDescription,
  useOnboardingTemplates,
} from '@/products/onboarding/features/use-onboarding-templates';
import { onboardingTemplatePath } from '@/products/onboarding/onboarding-paths';

export function OnboardingTemplatesPage() {
  usePageTitle('Vorlagen');
  const copy = useVoiceCopy();
  const me = useMe();
  const navigate = useNavigate();
  const canManage = hasPermission(me.data, PermissionCode.ONBOARDING_TEMPLATES_MANAGE);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const templates = useOnboardingTemplates({
    page,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const create = useCreateOnboardingTemplate();
  const draft = useCreateOnboardingTemplateFromDescription();

  const form = useForm<CreateOnboardingTemplateRequest>({
    resolver: zodResolver(
      createOnboardingTemplateRequestSchema,
    ) as Resolver<CreateOnboardingTemplateRequest>,
    defaultValues: {
      kind: 'onboarding',
      name: '',
      description: null,
    },
  });

  if (!canManage) {
    return <p className="text-sm text-muted-foreground">{copy('onboarding.access.denied')}</p>;
  }

  if (templates.isError && isForbidden(templates.error)) {
    return <p className="text-sm text-muted-foreground">{copy('onboarding.access.denied')}</p>;
  }

  async function onCreate(values: CreateOnboardingTemplateRequest) {
    try {
      const created = await create.mutateAsync(values);
      toast.success('Vorlage angelegt.');
      setCreateOpen(false);
      form.reset({ kind: 'onboarding', name: '', description: null });
      void navigate(onboardingTemplatePath(created.id));
    } catch (caught) {
      toast.error(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Die Vorlage konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Vorlagen"
        description="Phasen und Aufgaben für Eintritte und Austritte."
        action={
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => setImportOpen(true)}>
              Importieren
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDraftError(null);
                setDraftOpen(true);
              }}
            >
              Mit KI entwerfen
            </Button>
            <Button type="button" onClick={() => setCreateOpen(true)}>
              Vorlage anlegen
            </Button>
          </div>
        }
      />

      {templates.isPending ? (
        <p className="text-sm text-muted-foreground">Vorlagen werden geladen …</p>
      ) : null}

      {templates.isError ? (
        <p className="text-sm text-destructive">
          {templates.error instanceof ApiRequestError
            ? templates.error.message
            : 'Die Vorlagen konnten nicht geladen werden.'}
        </p>
      ) : null}

      {templates.data?.items.length === 0 ? (
        <p className={`${onboardingPanelClassName} text-sm text-muted-foreground`}>
          {copy('onboarding.templates.empty')}
        </p>
      ) : null}

      {templates.data && templates.data.items.length > 0 ? (
        <div className="space-y-3">
          <ul className={`${onboardingTableFrameClassName} divide-y`}>
            {templates.data.items.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3"
              >
                <div>
                  <Link
                    to={onboardingTemplatePath(item.id)}
                    className="font-medium hover:underline"
                  >
                    {item.name}
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    {ONBOARDING_CASE_KIND_LABEL[item.kind]}
                    {item.draftVersionId ? ' · Entwurf' : ''}
                    {item.currentPublishedVersionId ? ' · Veröffentlicht' : ''}
                    {!item.isActive ? ' · inaktiv' : ''}
                  </p>
                </div>
                <OnboardingTemplateDeleteButton template={item} />
              </li>
            ))}
          </ul>
          <ListPagination
            page={templates.data.page}
            pageSize={templates.data.pageSize}
            total={templates.data.total}
            onPageChange={setPage}
          />
        </div>
      ) : null}

      <OnboardingTemplateDraftDialog
        key={draftOpen ? 'draft-open' : 'draft-closed'}
        mode="create"
        open={draftOpen}
        pending={draft.isPending}
        error={draftError}
        onOpenChange={(open) => {
          setDraftOpen(open);
          if (!open) {
            setDraftError(null);
          }
        }}
        onCreate={(body) => {
          setDraftError(null);
          void draft
            .mutateAsync(body)
            .then((created) => {
              setDraftOpen(false);
              toast.success('Vorlage angelegt.');
              void navigate(onboardingTemplatePath(created.id));
            })
            .catch((caught: unknown) => {
              setDraftError(
                caught instanceof ApiRequestError
                  ? caught.message
                  : 'Die Vorlage konnte nicht angelegt werden.',
              );
            });
        }}
        onReplace={() => undefined}
      />

      <OnboardingTemplateImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={(id) => {
          toast.success('Vorlage importiert.');
          setImportOpen(false);
          void navigate(onboardingTemplatePath(id));
        }}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vorlage anlegen</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit(onCreate)();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="onboarding-tpl-name">Name</Label>
              <Input id="onboarding-tpl-name" {...form.register('name')} />
              {form.formState.errors.name?.message ? (
                <p className="text-sm text-destructive" role="alert">
                  {form.formState.errors.name.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="onboarding-tpl-kind">Art</Label>
              <Select
                value={form.watch('kind')}
                onValueChange={(value) =>
                  form.setValue('kind', value as CreateOnboardingTemplateRequest['kind'], {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
              >
                <SelectTrigger id="onboarding-tpl-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">
                    {ONBOARDING_CASE_KIND_LABEL.onboarding}
                  </SelectItem>
                  <SelectItem value="offboarding">
                    {ONBOARDING_CASE_KIND_LABEL.offboarding}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={create.isPending}>
                Anlegen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function isForbidden(error: unknown): boolean {
  return error instanceof ApiRequestError && error.error.statusCode === 403;
}
